import { UploadDownloadButtons } from '@components/_SiteWide/UploadStatus/UploadDownloadButtons'
import { useSecurityContext } from '@components/Navigation/SecurityContext'
import {
  addCommasToNumber,
  GraviGrid,
  useGridFetch,
  validateInt,
  validateNotEmptyString,
} from '@gravitate-js/excalibrr'
import api from '@utils/api'
import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { createTractorConfig } from './createConfig'
import { useUserSettings } from '@hooks/settings'
import { useTractors } from '@api/useTractors'
import { gridViewManager } from '@/utils/grid'

const STORAGE_KEY = 'tractor'

export function Tractor() {
  const [depots, setDepots] = useState()
  const { updateTractorMutation } = useTractors()
  const { mutate: updateMutation } = updateTractorMutation()
  const { rowData, refresh, isLoading } = useGridFetch(api.getTractors)

  useEffect(() => {
    api.getTractorMetadata().then(({ data }) => {
      setDepots(data.depots)
    })
  }, [])
  const selectOptions = {
    depots,
  }
  const { scopes } = useSecurityContext()
  const canEdit = scopes.admin?.vehicle_write
  const { updateSetting } = useUserSettings()
  const getRowId = useCallback((row) => row?.data?.id, [])
  const columnDefs = useMemo(() => (depots ? getColumnDefs(selectOptions) : []), [selectOptions, depots])
  const agPropOverrides = useMemo(
    () => ({
      getRowId,
    }),
    []
  )

  const createWrapper = useCallback(async (tractor) => {
    if (tractor?.tractor_number) tractor.tractor_number = tractor.tractor_number.toString()
    const { data: id } = await api.createTractor(tractor)
    return new Promise((resolve) => {
      resolve({ data: { ...tractor, id } })
    })
  }, [])

  return (
    <GraviGrid
      controlBarProps={{
        title: 'Tractor Management',
        actionButtons: (
          <UploadDownloadButtons
            downloadEP={api.downloadTractors}
            uploadEP={canEdit && api.uploadTractors}
            fileName='Tractor.xlsx'
            refreshCallback={refresh}
          />
        ),
      }}
      agPropOverrides={agPropOverrides}
      columnDefs={columnDefs}
      storageKey={STORAGE_KEY}
      rowData={rowData}
      shouldInsertCreated
      loading={isLoading}
      updateEP={canEdit && updateMutation}
      createEP={canEdit && createWrapper}
      createConfig={createTractorConfig}
      createSelectOptions={selectOptions}
      onGridConfigChanged={(config) => updateSetting(`gridConfig::${STORAGE_KEY}`, config)}
      {...gridViewManager(STORAGE_KEY, updateSetting)}
    />
  )
}

const getColumnDefs = (selectOptions) => [
  {
    field: 'tractor_number',
    headerName: 'Tractor Number',
    valueSetter: validateNotEmptyString,
  },
  {
    field: 'depot',
    cellEditorPopup: true,
    cellEditor: 'agRichSelectCellEditor',
    valueSetter: validateNotEmptyString,
    cellEditorParams: {
      values: selectOptions.depots,
    },
  },
  {
    field: 'vin',
    valueSetter: validateNotEmptyString,
  },
  {
    field: 'make',
    valueSetter: validateNotEmptyString,
  },
  {
    field: 'model',
    valueSetter: validateNotEmptyString,
  },
  {
    field: 'year',
    valueSetter: validateInt,
  },
  {
    field: 'weight',
    valueFormatter: ({ value }) => value && addCommasToNumber(value),
    valueSetter: validateInt,
  },
  {
    field: 'next_tractor_maintenance_required',
    headerName: 'Maint. Required',
    cellEditor: 'agSelectCellEditor',

    valueGetter: (params) => (params.data?.next_tractor_maintenance_required ? 'Yes' : 'No'),
    valueSetter: (params) => {
      params.data.next_tractor_maintenance_required = params.newValue === 'Yes'
    },
    cellEditorParams: {
      values: ['Yes', 'No'],
    },
  },
  {
    field: 'has_pump',
    headerName: 'Has Pump',
    cellEditor: 'agSelectCellEditor',
    valueGetter: (params) => (params.data?.has_pump ? 'Yes' : 'No'),
    valueSetter: (params) => {
      params.data.has_pump = params.newValue === 'Yes'
    },
    cellEditorParams: {
      values: ['Yes', 'No'],
    },
  },
]
